import { LitElement, html, css } from 'lit';
import { FetchMixin } from '../FetchMixin';

/**
 * ダッシュボードお知らせ一覧コンポーネント
 */
class DashboardNotificationList extends FetchMixin(LitElement) {
  static styles = css`
    :host {
      display: none; /* 初期状態では非表示 */
      position: fixed;
      /* top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      */
      right: 120px;
      top: 40px;
      z-index: 1001; /* z-index を高く設定 */
      width: 250px;
      background: rgba(0, 0, 0, 0.77); /* 背景色を追加 */
      border-radius: 4px;
      box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 6px;
    }

    .dashboard-notification-list-panel {
      max-height: 300px;
      max-width:300px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
    }

    .dashboard-notification-item {
      flex-grow: 1;
      color: white;
      padding-top: 12px;
      padding-right: 0px;
      padding-bottom: 12px;
      padding-left: 5px;
      color: rgb(255 255 255);
      font-size: 16px;
      cursor: pointer;
      transition: background-color 0.3s ease;
      border-bottom: 1px solid #dfdfdf;
      position: relative;
      width: 230px;
    }

    .dashboard-notification-item:hover {
      background-color: rgba(255, 255, 255, 0.7);
      color: rgb(0, 0, 0);
    }

    .dashboard-notification-item:hover .notification-date {
      color: rgb(0, 0, 0);
    }

    .notification-point {
      position: absolute;
      top: 0px;
      left: 5px;
      color: red;
      font-size: 12px;
    }

    .notification-date {
      position: absolute;
      bottom: 3px;
      right: 10px;
      font-size: 10px;
      color: lightgray;
    }
  `;

  static properties = {
    notifications: { type: Array },
    selectedNotificationId: { type: Number },
  };

  constructor() {
    super();
    this.notifications = [];
    this.selectedNotificationId = null;
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.refreshInterval = setInterval(() => this.fetchNotifications(), 5 * 60 * 1000);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('click', this.boundHandleClickOutside);
    window.addEventListener('notification-read', this.handleNotificationRead.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.boundHandleClickOutside);
    window.removeEventListener('notification-read', this.handleNotificationRead.bind(this));
    clearInterval(this.refreshInterval);
    super.disconnectedCallback();
  }

  initNotificationData(notifications) {
    this.notifications = notifications;
    const hasUnread = this.checkForUnreadNotifications();
    this.displayNotificationPoint(hasUnread);

    const event = new CustomEvent('unread-notification-status', {
      detail: { hasUnread },
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);
  }

  /**
   * 外部がクリックされたときの処理
   * @param {*} event 
   */
  handleClickOutside(event) {
    this.handleCloseMenu(event);
  }

  /**
   * メニューを閉じる
   * @param {*} event 
   */
  handleCloseMenu(event) {
    this.style.display = 'none';
  }

  /**
   * お知らせ一覧を取得
   */
  async fetchNotifications() {
    // fetchオプションを設定
    const options = {
      method: 'GET',
    };

    // .envからエンドポイントを読み込み
    const endpoint = process.env.NOTIFICATION_ENDPOINT;

    // APIを呼び出し
    try {
      let response = await this.fetchWithAuth(endpoint, options);

      if (response && response.status === 'notification_get_success') {
        this.notifications = response.details.notifications;
        // 未読のお知らせがあるかチェック
        //this.checkUnreadNotifications();
        const hasUnread = this.checkForUnreadNotifications();
        this.displayNotificationPoint(hasUnread);

        const event = new CustomEvent('unread-notification-status', {
          detail: { hasUnread },
          bubbles: true,
          composed: true
        });
        this.dispatchEvent(event);

      } else {
        //throw new Error('お知らせの取得に失敗しました');
        return Promise.reject('お知らせの取得に失敗しました');
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      //return Promise.reject(error);
    }
  }

  /**
 * 未読のお知らせがあるかチェックする
 * @returns {boolean} 未読のお知らせがあるかどうか
 */
  checkForUnreadNotifications() {
    return Object.values(this.notifications).some(notification => !notification.isRead);
  }

  /**
   * ポイントを表示する
   * @param {boolean} hasUnread 未読のお知らせがあるかどうか
   */
  displayNotificationPoint(hasUnread) {
    const notificationPoint = this.shadowRoot.querySelector('.notification-point');
    if (notificationPoint) {
      notificationPoint.style.display = hasUnread ? 'block' : 'none';
    }
  }

  /**
    * お知らせタイトルクリック時の処理
    * @param {number} notificationId 
    * @param {boolean} isRead 
    */
  handleNotificationClick(notificationId, isRead) {
    // 選択されたお知らせの詳細情報を取得
    const selectedNotification = this.notifications.find(notification => notification.notificationId === notificationId);

    // イベントを発行して、DashboardNotificationDetail コンポーネントに通知詳細を渡す
    const event = new CustomEvent('notification-selected', {
      detail: { notification: selectedNotification }, // 詳細情報を含める
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);
  }

  /**
   * お知らせ既読イベント処理
   * @param {CustomEvent} event 
   */
  handleNotificationRead(event) {
    const notificationId = event.detail.notificationId;
    const notificationIndex = this.notifications.findIndex(notification => notification.notificationId === notificationId);
    if (notificationIndex !== -1) {
      this.notifications[notificationIndex].isRead = true; // isReadを更新
      this.requestUpdate(); // コンポーネントを再レンダリング
      //this.checkUnreadNotifications(); // 未読ポイントの表示を更新
      const hasUnread = this.checkForUnreadNotifications();
      this.displayNotificationPoint(hasUnread);

      const event = new CustomEvent('unread-notification-status', {
        detail: { hasUnread },
        bubbles: true,
        composed: true
      });
      this.dispatchEvent(event);
    }
  }

  render() {
    return html`
      <div class="dashboard-notification-list-panel">
        ${this.notifications.map(notification => html`
          <div 
            class="dashboard-notification-item" 
            data-notification-id="${notification.notificationId}" 
            @click="${() => this.handleNotificationClick(notification.notificationId, notification.isRead)}">
            <div class="notification-title">${notification.title}</div>
            ${!notification.isRead ? html`<div class="notification-point">●</div>` : ''}
            <div class="notification-date">${notification.createdDate}</div> <div class="notification-date">${notification.createdDate}</div> 
          </div>
        `)}
        ${this.notifications.length === 0 ? html`<div class="dashboard-notification-item">お知らせはありません</div>` : ''}
      </div>
    `;
  }
}

customElements.define('dashboard-notification-list', DashboardNotificationList);